import React from 'react'
import { AlertTriangle, CreditCard, Crown, Zap } from 'lucide-react'
import { getPlanDisplayName, formatPrice, calculatePlanPrice } from '../lib/billing'

interface TrialExpiredModalProps {
  isOpen: boolean
  memberCount: number
  onUpgrade: (planName: string) => void
  upgrading: string | null
}

export const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  isOpen,
  memberCount,
  onUpgrade,
  upgrading
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Your Trial Has Expired
          </h3>
          
          <p className="text-sm text-gray-600 mb-6">
            Your 7-day free trial has ended. Please choose a plan to continue using SyncSimple.
          </p>

          <div className="space-y-4 mb-6">
            {/* Essentials Plan */}
            <div className="border border-gray-200 rounded-lg p-4 text-left">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Crown className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{getPlanDisplayName('essentials')}</h4>
                  <p className="text-sm text-gray-500">
                    {formatPrice(1.50)}/month
                  </p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-3">
                • Up to 4 team members<br/>
                • AI-powered daily summaries<br/>
                • Priority support
              </div>

              <button
                onClick={() => onUpgrade('essentials')}
                disabled={upgrading === 'essentials'}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {upgrading === 'essentials' ? 'Processing...' : 'Choose Essentials'}
              </button>
            </div>

            {/* Advanced Plan */}
            <div className="border border-gray-200 rounded-lg p-4 text-left">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Zap className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{getPlanDisplayName('advanced')}</h4>
                  <p className="text-sm text-gray-500">
                    {formatPrice(calculatePlanPrice('advanced', memberCount))}/user/month
                  </p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-3">
                • Unlimited team members<br/>
                • Advanced analytics & insights<br/>
                • Premium support
              </div>

              <button
                onClick={() => onUpgrade('advanced')}
                disabled={upgrading === 'advanced'}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {upgrading === 'advanced' ? 'Processing...' : 'Choose Advanced'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center text-sm text-gray-500">
            <CreditCard className="h-4 w-4 mr-2" />
            <span>Secure payment powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  )
}